import { Bookmark, FileText } from "lucide-react";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { useSelector } from "react-redux";
import { useState, useMemo } from "react";

export function NotesTab() {
  const comments = useSelector((state) => state.intakeData.comments);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("All");

  // Derive unique section_ids
  const sectionTabs = useMemo(() => {
    const unique = new Set(comments.map((c) => c.section_id));
    return ["All", ...Array.from(unique)];
  }, [comments]);

  const filteredComments = comments.filter((comment) => {
    const matchesSearch = comment.text
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === "All" || comment.section_id === activeTab;
    return matchesSearch && matchesTab;
  });

  return (
    <Card className="p-5 bg-white shadow-md">
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-base font-semibold flex items-center">
          <FileText className="h-4 w-4 mr-2 text-[#FF5533]" />
          Application Notes
        </h3>
        <Button variant="outline" size="sm">
          <Bookmark className="h-4 w-4 mr-1" />
          Add Note
        </Button>
      </div>

      {/* Search + Tabs */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-6">
        <Input
          placeholder="Search notes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full sm:w-64"
        />

        <div className="flex gap-2 overflow-x-auto">
          {sectionTabs.map((tab) => (
            <Button
              key={tab}
              size="sm"
              variant={activeTab === tab ? "default" : "outline"}
              onClick={() => setActiveTab(tab)}
              className={`whitespace-nowrap rounded-full px-4 py-1 text-sm transition-colors ${
                activeTab === tab
                  ? "bg-[#FF5533] text-white hover:bg-[#e14c2e]"
                  : "border border-gray-300 text-gray-700 hover:bg-gray-100"
              }`}
            >
              {tab}
              {tab === "All" && (
                <span className="ml-1 text-xs text-muted-foreground">
                  ({comments.length})
                </span>
              )}
            </Button>
          ))}
        </div>
      </div>

      {/* Notes list */}
      <div className="space-y-4">
        {filteredComments.map((comment) => {
          const created = new Date(comment.created_at).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "numeric",
          });

          return (
            <div key={comment.id} className="p-4 border rounded-md">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h4 className="font-medium">{comment.text}</h4>
                  <p className="text-sm text-gray-500">
                    Added by {comment.user.name} • {created}
                  </p>
                </div>
                <Badge variant="secondary">{comment.section_id}</Badge>
              </div>
              <p className="text-gray-700 text-sm">
                Tab: <span className="font-medium">{comment.tab}</span>
              </p>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
